import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CreateLearningVideoDto, UpdateLearningVideoDto } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';

interface LearningVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateLearningVideoDto | UpdateLearningVideoDto) => void;
  sectionId?: string | null;
  video?: any;
  isLoading?: boolean;
}

const LearningVideoModal: React.FC<LearningVideoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  sectionId,
  video,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    url: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (video) {
      setFormData({
        title: video.title,
        url: video.url,
      });
    } else {
      setFormData({
        title: '',
        url: '',
      });
    }
    setErrors({});
  }, [video, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Video title is required';
    } else if (formData.title.trim().length < 2) {
      newErrors.title = 'Video title must be at least 2 characters';
    }

    if (!formData.url.trim()) {
      newErrors.url = 'Video URL is required';
    } else {
      // Basic URL validation
      try {
        new URL(formData.url);
      } catch {
        newErrors.url = 'Please provide a valid URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      title: formData.title.trim(),
      url: formData.url.trim(),
      ...(sectionId && { sectionId }),
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const extractVideoTitle = (url: string) => {
    // Try to extract title from YouTube URL
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      const videoId = urlParams.get('v') || url.split('/').pop();
      if (videoId && !formData.title) {
        // You could make an API call to get the actual title, but for now just suggest a format
        return `YouTube Video ${videoId}`;
      }
    }
    return '';
  };

  const handleUrlChange = (url: string) => {
    handleInputChange('url', url);
    
    // Auto-suggest title if empty
    if (!formData.title && url) {
      const suggestedTitle = extractVideoTitle(url);
      if (suggestedTitle) {
        setFormData(prev => ({ ...prev, title: suggestedTitle }));
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {video ? 'Edit Video' : 'Add Video'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Video Title *
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.title ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter video title"
              disabled={isLoading}
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
              Video URL *
            </label>
            <input
              type="url"
              id="url"
              value={formData.url}
              onChange={(e) => handleUrlChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.url ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="https://www.youtube.com/watch?v=..."
              disabled={isLoading}
            />
            {errors.url && <p className="mt-1 text-sm text-red-600">{errors.url}</p>}
            <p className="mt-1 text-xs text-gray-500">
              Supports YouTube, Vimeo, and other video platforms
            </p>
          </div>

          {/* Video Preview */}
          {formData.url && !errors.url && (
            <div className="border border-gray-200 rounded-md p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
              <div className="text-sm text-gray-600">
                <p className="font-medium">{formData.title || 'Untitled Video'}</p>
                <p className="text-xs text-gray-500 truncate">{formData.url}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              disabled={isLoading}
            >
              {isLoading && <LoadingSpinner size="sm" />}
              <span>{video ? 'Update' : 'Add'} Video</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LearningVideoModal;
